import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ResponsiveTable from '../components/ResponsiveTable';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/permissions';
import { formatCurrency } from '../utils/currency';
import { getClientTabPath, openBalanceListNavState } from '../utils/navigationReturn';
import { usePageNotice } from '../context/FeedbackContext';

function clientDisplayName(row) {
  const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
  return name || row.email || row.companyName || '—';
}

function BalanceRowActions({ to, state, label, newTabLabel }) {
  return (
    <Box display="flex" alignItems="center" gap={0.5} onClick={(event) => event.stopPropagation()}>
      <Button
        size="small"
        variant="outlined"
        component={RouterLink}
        to={to}
        state={state}
        data-testid="open-balance-view"
      >
        {label}
      </Button>
      <Tooltip title={newTabLabel}>
        <IconButton
          component={RouterLink}
          to={to}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          aria-label={newTabLabel}
          data-testid="open-balance-view-new-tab"
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

const DashboardMetricOpenBalance = () => {
  const { t } = useTranslation();
  const { userRole, customPermissions } = useAuth();
  const canAccess = hasPermission(userRole, 'view_dashboard_open_balance', customPermissions);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState({
    metricsCurrency: 'USD',
    totalOpenBalance: 0,
    calculatedAt: null,
    clients: [],
  });
  const [sortBy, setSortBy] = useState('openBalance');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/dashboard/metrics/open-balance/clients');
      setPayload(res.data || { clients: [], totalOpenBalance: 0 });
    } catch (err) {
      setError(
        err.userMessage
        || err.response?.data?.error
        || t('dashboard.metricsDetail.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (canAccess) fetchData();
  }, [canAccess, fetchData]);

  usePageNotice(
    'dashboard-open-balance',
    error ? { message: error, severity: 'error', persist: true } : null,
  );

  const currency = payload.metricsCurrency || 'USD';

  const sortedClients = useMemo(() => {
    const list = [...(payload.clients || [])];
    const dir = sortOrder === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortBy === 'openBalance') {
        return (a.openBalance - b.openBalance) * dir;
      }
      const aName = clientDisplayName(a).toLowerCase();
      const bName = clientDisplayName(b).toLowerCase();
      if (aName < bName) return -1 * dir;
      if (aName > bName) return 1 * dir;
      return 0;
    });
    return list;
  }, [payload.clients, sortBy, sortOrder]);

  const clientIds = useMemo(() => sortedClients.map((row) => row.id), [sortedClients]);

  const balanceState = (row) => openBalanceListNavState(clientIds, row.id);

  if (!canAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const formatMoney = (n) => formatCurrency(n, currency);

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(columnId);
      setSortOrder(columnId === 'openBalance' ? 'desc' : 'asc');
    }
  };

  const columns = [
    {
      id: 'client',
      label: t('dashboard.metricsDetail.client'),
      sortable: true,
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {clientDisplayName(row)}
          </Typography>
          {row.companyName && (
            <Typography variant="caption" color="text.secondary" display="block">
              {row.companyName}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            {row.email}
          </Typography>
          {row.disabled && (
            <Chip label={t('users.disabled')} size="small" sx={{ mt: 0.5 }} />
          )}
        </Box>
      ),
    },
    {
      id: 'openBalance',
      label: t('dashboard.metricsDetail.openBalance'),
      sortable: true,
      render: (row) => (
        <Typography variant="body2" fontWeight={600} color="error.main">
          {formatMoney(row.openBalance)}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: t('common.actions'),
      stickyRight: true,
      render: (row) => (
        <BalanceRowActions
          to={getClientTabPath(row.id, 'balance')}
          state={balanceState(row)}
          label={t('dashboard.metricsDetail.viewBalance')}
          newTabLabel={t('dashboard.metricsDetail.openInNewTab')}
        />
      ),
    },
  ];

  const renderMobileCard = (row) => (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" fontWeight={600}>
          {clientDisplayName(row)}
        </Typography>
        {row.companyName && (
          <Typography variant="body2" color="text.secondary">
            {row.companyName}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {row.email}
        </Typography>
        <Typography variant="body1" fontWeight={700} color="error.main">
          {formatMoney(row.openBalance)}
        </Typography>
        {row.disabled && (
          <Chip label={t('users.disabled')} size="small" sx={{ mt: 1 }} />
        )}
        <Box sx={{ mt: 1.5 }}>
          <BalanceRowActions
            to={getClientTabPath(row.id, 'balance')}
            state={balanceState(row)}
            label={t('dashboard.metricsDetail.viewBalance')}
            newTabLabel={t('dashboard.metricsDetail.openInNewTab')}
          />
        </Box>
      </CardContent>
    </Card>
  );

  const calculatedAtLabel = payload.calculatedAt
    ? t('dashboard.metricsLastUpdated', {
      date: new Date(payload.calculatedAt).toLocaleString(),
    })
    : t('dashboard.metricsDetail.liveTotals');

  return (
    <Layout>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Button
          component={RouterLink}
          to="/dashboard"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
          size="small"
        >
          {t('dashboard.metricsDetail.backToDashboard')}
        </Button>

        <Typography variant="h5" gutterBottom>
          {t('dashboard.revenue.openBalance')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('dashboard.metricsDetail.openBalanceSubtitle')}
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  {t('dashboard.metricsDetail.companyTotal')}
                </Typography>
                <Typography variant="h4" fontWeight={700} color="error.main">
                  {formatMoney(payload.totalOpenBalance)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {calculatedAtLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('dashboard.metricsDetail.liveBreakdownHint')}
                </Typography>
              </CardContent>
            </Card>

            {sortedClients.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {t('dashboard.metricsDetail.noClientsWithBalance')}
              </Typography>
            ) : (
              <ResponsiveTable
                columns={columns}
                data={sortedClients}
                renderMobileCard={renderMobileCard}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
                getStatusColor={() => 'default'}
              />
            )}
          </>
        )}
      </Box>
    </Layout>
  );
};

export default DashboardMetricOpenBalance;
